import type { AnyExtension } from "@tiptap/core";
import CharacterCount from "@tiptap/extension-character-count";
import { Color } from "@tiptap/extension-color";
import FileHandler from "@tiptap/extension-file-handler";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import { Table } from "@tiptap/extension-table";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableRow } from "@tiptap/extension-table-row";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { Markdown } from "@tiptap/markdown";
import StarterKit from "@tiptap/starter-kit";

import { DEFAULT_CODE_BLOCK_LANGUAGE, lowlight } from "./code-block";
import { editorT } from "./i18n";
import { IMAGE_UPLOAD_ACCEPTED_MIME_TYPES } from "../config/uploads";
import { BlockStyle } from "../extensions/block-style-extension";
import { EditorCodeBlock } from "../extensions/code-block/extension";
import { EditorEmoji, MarkdownEmoji } from "../extensions/emoji-suggestion";
import { EditorHorizontalRule } from "../extensions/horizontal-rule";
import { EditorImage } from "../extensions/image-extension";
import { createPendingImageUploadBatch, ImageUploadNode } from "../extensions/image-upload/extension";
import { Indent } from "../extensions/indent-extension";
import { MarkdownShortcuts } from "../extensions/markdown-shortcuts";
import { SlashCommand } from "../extensions/slash-command";
import { EditorTableCell, EditorTableHeader } from "../extensions/table-extensions";
import { VideoEmbed, VideoEmbedInput } from "../extensions/video-embed/extension";
import type { NotionEditorProps } from "../types";
import { getEditorModeCapabilities } from "./editor-mode";

export const createEditorExtensions = ({
  contentMode = "tiptap-json",
  uploadImage,
}: Pick<NotionEditorProps, "contentMode" | "uploadImage">) => {
  const capabilities = getEditorModeCapabilities(contentMode);
  const extensions: AnyExtension[] = [
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      codeBlock: false,
      horizontalRule: false,
      link: false,
      underline: false,
      dropcursor: {
        color: "rgba(35, 131, 226, 0.4)",
        width: 3,
      },
    }),
    CharacterCount,
    EditorHorizontalRule,
    Placeholder.configure({
      placeholder: () => editorT("placeholder.empty"),
      emptyNodeClass: "is-empty",
      emptyEditorClass: "is-editor-empty",
    }),
    TaskList,
    TaskItem.configure({ nested: true }),
    EditorImage.configure({
      resize: {
        enabled: capabilities.imageResize,
        directions: ["left", "right"],
        minWidth: 120,
        minHeight: 80,
        alwaysPreserveAspectRatio: true,
      },
    }),
    EditorCodeBlock.configure({
      lowlight,
      defaultLanguage: DEFAULT_CODE_BLOCK_LANGUAGE,
      enableTabIndentation: true,
      tabSize: 2,
      HTMLAttributes: {
        class: "code-block",
      },
    }),
    contentMode === "markdown" ? MarkdownEmoji : EditorEmoji,
    SlashCommand.configure({ contentMode }),
    Link.configure({
      autolink: capabilities.automaticLinks,
      defaultProtocol: "https",
      linkOnPaste: capabilities.linkOnPaste,
      openOnClick: false,
    }),
    MarkdownShortcuts,
    Table.configure({
      resizable: capabilities.tableResize,
      allowTableNodeSelection: capabilities.tableResize,
    }),
    TableRow,
    ...(contentMode === "markdown" ? [TableHeader, TableCell] : [EditorTableHeader, EditorTableCell]),
  ];

  extensions.push(
    Markdown.configure({
      markedOptions: {
        breaks: false,
        gfm: true,
      },
    }),
  );

  if (capabilities.imageUpload) {
    extensions.push(
      ImageUploadNode.configure({ upload: uploadImage }),
      FileHandler.configure({
        allowedMimeTypes: [...IMAGE_UPLOAD_ACCEPTED_MIME_TYPES],
        onPaste: (editor, files) => {
          const uploadBatchId = createPendingImageUploadBatch(files);

          editor
            .chain()
            .focus()
            .insertContent({
              type: "imageUpload",
              attrs: { uploadBatchId },
            })
            .run();
        },
      }),
    );
  }

  if (capabilities.videoEmbed) {
    extensions.push(VideoEmbed, VideoEmbedInput);
  }

  if (capabilities.advancedFormatting) {
    extensions.push(
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      BlockStyle,
      Underline,
      Superscript,
      Subscript,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Indent,
    );
  }

  return extensions;
};
